// assetLoadProgress — pub/sub store for the avatar boot-DOWNLOAD progress bar.
//
// Meters the big binary downloads that otherwise leave the screen blank on a
// slow connection: the ONNX Runtime wasm, riglogic.wasm, the /ogs decoders
// and hair.gsh. Each download is a byte "task"; AvatarLoadOverlay
// reads the aggregate to draw one determinate bar + a live "what's downloading
// now" status line. Mirrors the hairLoadProgress pub/sub shape.
//
// The two fetch choke points report here:
//   - staticAssetCache.js  (ort-wasm + riglogic.wasm, total from Content-Length)
//   - aamAssetCache.js      (ogs files + hair.gsh, total from the /ogs row size)
// Progress is per-FILE, not per-byte: each task reports register → complete
// (or markCached for a cache hit / fail on a bad response). The bar therefore
// advances in file-sized steps — the big downloads run concurrently, so a
// finer-grained stream buys little and cost a second body-reading clone we
// deliberately avoid (see aamAssetCache.js). A cache hit is born complete
// (markCached) so the bar never stalls on a task that never streams.

let _tasks = new Map()   // id -> { label, total, received, done, failed }
let _current = ''        // label of the most recently active task (status line)
let _listeners = []
let _clearTimer = null

// After the last task completes, hold the (100%) bar briefly so it doesn't
// flicker off in the gap before the next phase (e.g. hair init) starts.
const CLEAR_GRACE_MS = 1200

function computeAllDone() {
  let hasTasks = false, allDone = true
  for (const t of _tasks.values()) {
    if (t.failed) continue
    hasTasks = true
    if (!t.done) allDone = false
  }
  return { hasTasks, allDone }
}

function snapshot() {
  let total = 0, received = 0
  for (const t of _tasks.values()) {
    if (t.failed) continue
    // A task with an unknown total (no Content-Length) contributes to NEITHER
    // side — otherwise its received would inflate the numerator past 100%. It's
    // still reflected in the status line + the indeterminate flag below.
    if (t.total > 0) {
      total += t.total
      received += Math.min(t.received || 0, t.total)
    }
  }
  const { hasTasks, allDone } = computeAllDone()
  let fraction = total > 0 ? received / total : 0
  if (!allDone) fraction = Math.min(fraction, 0.99) // never show 100% while a download is still running
  return {
    active: hasTasks && (!allDone || _clearTimer != null),
    fraction,
    label: _current,
    receivedBytes: received,
    totalBytes: total,
    indeterminate: hasTasks && total === 0, // no Content-Length on any task → stripe
  }
}

function notify() { const s = snapshot(); for (const fn of _listeners) fn(s) }

function cancelClear() { if (_clearTimer != null) { clearTimeout(_clearTimer); _clearTimer = null } }

function scheduleClearIfDone() {
  const { hasTasks, allDone } = computeAllDone()
  if (hasTasks && allDone && _clearTimer == null) {
    _clearTimer = setTimeout(() => {
      _clearTimer = null; _tasks = new Map(); _current = ''; notify()
    }, CLEAR_GRACE_MS)
  }
}

export const assetLoadProgress = {
  subscribe(fn) { _listeners.push(fn); fn(snapshot()); return () => { _listeners = _listeners.filter((l) => l !== fn) } },
  get() { return snapshot() },

  // Register (or refresh) a task. Idempotent by id — StrictMode double-mounts
  // and retries just re-touch the same entry, never double-count.
  register(id, label, total = 0) {
    cancelClear()
    const prev = _tasks.get(id)
    _tasks.set(id, {
      label, total: total || prev?.total || 0,
      received: prev?.received || 0, done: prev?.done || false, failed: false,
    })
    if (label) _current = label
    notify()
  },

  complete(id) {
    const t = _tasks.get(id); if (!t) return
    t.done = true; t.received = t.total || t.received
    scheduleClearIfDone() // set the grace timer BEFORE notify so `active` stays true
    notify()
  },

  fail(id) {
    const t = _tasks.get(id); if (t) t.failed = true
    scheduleClearIfDone()
    notify()
  },

  // Convenience: a cache HIT — the bytes are already local, so the task is born
  // complete (keeps the aggregate honest without a fake download).
  markCached(id, label, total = 0) {
    this.register(id, label, total)
    this.complete(id)
  },

  // Test/debug hook — drop all state (and any pending clear timer).
  _reset() { cancelClear(); _tasks = new Map(); _current = ''; notify() },
}

if (typeof window !== 'undefined') window.__ASSET_LOAD_PROGRESS__ = assetLoadProgress
